import { combineLatest, EMPTY, forkJoin, Observable, of, take, zip } from 'rxjs';
import { catchError, expand, map, reduce, shareReplay, switchMap } from 'rxjs/operators';

import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AssociationInterface,
  CreateAssociationResponse,
  CRMAssociationApiService,
  DeleteAssociationRequestInterface,
  FilterGroupOperator,
  FilterOperator,
  ListAssociationsRequestInterface,
  ListAssociationsResponseInterface,
  TagInterface,
  CrmObjectInterface,
} from '@vendasta/crm';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';
import { CompanyContactCacheService } from '../../company-contact-cache.service';
import { getAssociationDirection, getAssociationType } from '../../association.utils';
import { CrmObjectService } from './crm-object.service';
import { AssociationDirection, AssociationType } from '../../../constants';

export interface Association {
  associationId: string;
  associationType: string;
  tags: string[];
  associatedObject: CrmObjectInterface;
}

export interface SelectedObjects {
  crmObjects: CrmObjectInterface[];
  objects: { id: string; name?: string }[];
}

export interface CreateAssocResponse {
  createdCount: string;
  totalCount: string;
  error: boolean;
}

type associationDirection = 'from' | 'to';

@Injectable()
export class CrmAssociationService {
  private static readonly CHUNK_SIZE = 50;

  crmObjectIdToAssociate = '';
  copyCrmObjects: SelectedObjects = { crmObjects: [], objects: [] };

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly objectService: CrmObjectService,
    private readonly crmAssociationApiService: CRMAssociationApiService,
    public modal: MatDialog,
    protected readonly companyContactCacheService: CompanyContactCacheService,
  ) {}

  setCopyCrmObjects(objects: SelectedObjects): void {
    this.copyCrmObjects = objects;
  }

  setCrmObjectIdToAssociate(crmObjectIdToAssociate: string): void {
    this.crmObjectIdToAssociate = crmObjectIdToAssociate;
  }

  getRemainingContactAssociationsForCompanies$(companyId: string): Observable<Association[]> {
    const namespace$ = this.config.namespace$;
    const associations$: Observable<AssociationInterface[]> = namespace$.pipe(
      switchMap((namespace: string) => {
        return of({
          associations: [],
          pagingMetadata: {
            hasMore: true,
            nextCursor: '',
          },
        }).pipe(
          expand((resp: ListAssociationsResponseInterface) => {
            if (!resp.pagingMetadata?.hasMore) {
              return EMPTY;
            }
            return this.crmAssociationApiService.listAssociation({
              namespace: namespace,
              pagingOptions: {
                pageSize: 25,
                cursor: resp.pagingMetadata?.nextCursor,
              },
              filtersV2: {
                operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
                filters: [
                  {
                    fieldId: 'to_id',
                    operator: FilterOperator.FILTER_OPERATOR_IS,
                    values: [
                      {
                        string: companyId,
                      },
                    ],
                  },
                  {
                    fieldId: 'association_type',
                    operator: FilterOperator.FILTER_OPERATOR_IS,
                    values: [
                      {
                        string: 'Contact-Company',
                      },
                    ],
                  },
                ],
              },
            } as ListAssociationsRequestInterface);
          }),
          take(9), // Limiting here to avoid breaking the interface (200 users)
          reduce(
            (acc: AssociationInterface[], val: ListAssociationsResponseInterface): AssociationInterface[] => [
              ...acc,
              ...(val?.associations || []),
            ],
            [] as AssociationInterface[],
          ),
        );
      }),
    );
    const contacts$: Observable<CrmObjectInterface[]> = associations$.pipe(
      switchMap((response) => {
        if (!response?.length) {
          return of([] as CrmObjectInterface[]);
        }
        const contactIds = response.map((a) => a?.fromId || '').filter(Boolean);
        if (contactIds.length === 0) {
          return of([] as CrmObjectInterface[]);
        }
        return this.getMultiObjectChunked$('Contact', contactIds);
      }),
    );

    return combineLatest([associations$, contacts$]).pipe(
      map(([associations, contacts]) => {
        if (!contacts?.length) {
          return [];
        }

        return associations?.map((association) => {
          const contact = contacts?.find((contact) => {
            return contact?.crmObjectId === association.fromId;
          });
          const tags = association.tags?.map((tag) => {
            return tag.tagValue;
          });
          return {
            associationId: association.associationId,
            associationType: association.associationType,
            tags: tags,
            associatedObject: contact,
          } as Association;
        });
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  getChildCompanyAssociationsForCompany$(companyID: string): Observable<Association[]> {
    const namespace$ = this.config.namespace$;

    const associations$ = namespace$.pipe(
      switchMap((namespace) =>
        this.paginateListAssociation$(
          namespace,
          {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: [
              {
                fieldId: 'to_id',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [{ string: companyID }],
              },
              {
                fieldId: 'association_type',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [{ string: 'Company-Company' }],
              },
              {
                fieldId: 'tags',
                operator: FilterOperator.FILTER_OPERATOR_IS_ANY,
                values: [{ string: 'Parent' }],
              },
            ],
          },
          50,
        ),
      ),
    );

    const companies$ = this.getObjectsForAssociations$('Company', associations$, AssociationDirection.From);
    return this.joinAssociationsWithObjects$(associations$, companies$, 'from');
  }

  getParentCompanyAssociationsForCompany$(companyID: string): Observable<Association[]> {
    const namespace$ = this.config.namespace$;

    const associations$ = namespace$.pipe(
      switchMap((namespace) => {
        return this.crmAssociationApiService.listAssociation({
          namespace: namespace,
          pagingOptions: {
            pageSize: 1,
          },
          filtersV2: {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: [
              {
                fieldId: 'from_id',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [
                  {
                    string: companyID,
                  },
                ],
              },
              {
                fieldId: 'association_type',
                operator: FilterOperator.FILTER_OPERATOR_IS,
                values: [
                  {
                    string: 'Company-Company',
                  },
                ],
              },
              {
                fieldId: 'tags',
                operator: FilterOperator.FILTER_OPERATOR_IS_ANY,
                values: [
                  {
                    string: 'Parent',
                  },
                ],
              },
            ],
          },
        } as ListAssociationsRequestInterface);
      }),
      map((resp) => (resp?.associations ? resp.associations : [])),
    );

    const companies$ = this.getObjectsForAssociations$('Company', associations$, AssociationDirection.To);
    return this.joinAssociationsWithObjects$(associations$, companies$, 'to');
  }

  private joinAssociationsWithObjects$(
    associations$: Observable<AssociationInterface[]>,
    objects$: Observable<CrmObjectInterface[]>,
    direction?: associationDirection,
  ) {
    return combineLatest([associations$, objects$]).pipe(
      map(([associations, objects]) => {
        if (!objects?.length) {
          return [];
        }
        return associations.map((association) => {
          const object = objects.find((object) => {
            if (direction === 'from') {
              return object?.crmObjectId === association.fromId;
            }
            return object?.crmObjectId === association.toId;
          });
          const tags = association.tags?.map((tag) => {
            return tag.tagValue;
          });
          return {
            associationId: association.associationId,
            associationType: association.associationType,
            tags: tags,
            associatedObject: object,
          } as Association;
        });
      }),
    );
  }

  getCachedContactAssociationsForCompany$(companyId: string): Observable<Association[]> {
    let cachedContacts = this.companyContactCacheService.get(companyId);
    if (!cachedContacts) {
      const associatedContacts = this.getRemainingContactAssociationsForCompanies$(companyId);
      this.companyContactCacheService.set(companyId, associatedContacts);
      cachedContacts = associatedContacts;
    }
    return cachedContacts;
  }

  removeAssociation(id: string): Observable<boolean> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        return this.crmAssociationApiService.deleteAssociation({
          associationId: id,
          namespace: namespace,
        } as DeleteAssociationRequestInterface);
      }),
      map(() => {
        return true;
      }),
      take(1),
    );
  }

  createContactCompanyAssociation$(
    associationType: string,
    primarySelection?: string | null,
    tagMap?: Map<string, string[]> | null,
  ): Observable<{ respCount: CreateAssocResponse; copyCrmObjectsUpdated: Association[] }> {
    const primaryAssociation = new Map<string, boolean>();
    const associationTagValues = new Map<string, string[]>();

    const savedData$ = this.copyCrmObjects.objects.map((row) => {
      const association = {
        associationType: associationType,
        fromId: this.crmObjectIdToAssociate,
        toId: row.id,
        tags: [] as { tagValue: string }[],
      };

      if (row.id === primarySelection) {
        association.tags.push({
          tagValue: 'Primary',
        });
        primaryAssociation.set(row.id, true);
      }

      if (tagMap) {
        const tags = tagMap?.get(row.id);
        if (tags) {
          tags.forEach((tag) => {
            association.tags.push({
              tagValue: tag,
            });
          });
        }
      }

      associationTagValues.set(
        row.id,
        association.tags.map((tag) => tag.tagValue),
      );

      return this.config.namespace$.pipe(
        take(1),
        switchMap((namespace) => {
          return this.crmAssociationApiService.createAssociation({ namespace, association });
        }),
        catchError((error) => {
          console.error(error);
          return of(null);
        }),
      );
    });
    const callEndpoint = zip(...savedData$);
    return callEndpoint.pipe(
      take(1),
      map((data) => {
        let errorOccurred = false;
        const checkError = data.map((item) => {
          if (item === null) {
            errorOccurred = true;
            return null;
          }
          return item;
        });
        const filteredData = data.filter((item) => item !== null);
        const filteredCopy = this.copyCrmObjects.crmObjects.filter((_, index) => checkError[index] !== null);
        const respCount = {
          createdCount: String(filteredData.length),
          totalCount: String(data.length),
          error: errorOccurred,
        } as CreateAssocResponse;
        const copyCrmObjectsUpdated = filteredCopy.map((obj, index) => {
          return {
            ...obj,
            associationId: filteredData[index]?.associationId ?? '',
            tags: associationTagValues.get(obj.crmObjectId ?? '') || [],
          } as Association;
        });

        return { respCount, copyCrmObjectsUpdated };
      }),
    );
  }

  createAssociation$(
    associationType: string,
    tags: string[],
    fallbackToNoTags: boolean,
    direction: associationDirection = 'from',
  ): Observable<{
    respCount: CreateAssocResponse;
    copyCrmObjectsUpdated: Association[];
  }> {
    const savedData$ = this.copyCrmObjects.objects.map((row) => {
      const associationTags: TagInterface[] = [];
      for (const tag of tags) {
        associationTags.push({ tagValue: tag } as TagInterface);
      }
      const association = {
        associationType: associationType,
        fromId: row.id,
        toId: this.crmObjectIdToAssociate,
        tags: associationTags,
      };
      if (direction === 'to') {
        association.fromId = this.crmObjectIdToAssociate;
        association.toId = row.id;
      }

      return this.config.namespace$.pipe(
        take(1),
        switchMap((namespace) => {
          // Try creating the association with the provided namespace and association, and retry once if there's an error
          return this.crmAssociationApiService.createAssociation({ namespace, association }).pipe(
            catchError(() => {
              if (!fallbackToNoTags) {
                // If fallbackToNoTags is false, return null as nill and handeled in the next steps
                return of(null);
              }
              association.tags = [];
              return this.crmAssociationApiService.createAssociation({ namespace, association }).pipe(
                catchError(() => {
                  // If there's another error during the second attempt, return null as nill and handeled in the next steps
                  return of(null);
                }),
              );
            }),
          );
        }),
      );
    });

    const callEndpoint = zip(...savedData$);
    return callEndpoint.pipe(
      take(1),
      map((data) => {
        let errorOccurred = false;
        const checkError = data.map((item) => {
          if (item === null) {
            errorOccurred = true;
            return null;
          }
          return item;
        });
        const filteredData = data.filter((item) => item !== null);
        const filteredCopy = this.copyCrmObjects.crmObjects.filter((_, index) => checkError[index] !== null);
        const respCount = {
          createdCount: String(filteredData.length),
          totalCount: String(data.length),
          error: errorOccurred,
        } as CreateAssocResponse;
        const copyCrmObjectsUpdated = filteredCopy.map((obj, index) => {
          return {
            ...obj,
            associationId: filteredData[index]?.associationId ?? '',
          } as Association;
        });

        return { respCount, copyCrmObjectsUpdated };
      }),
      map((copyCrmObjectsUpdated) => {
        return copyCrmObjectsUpdated;
      }),
    );
  }

  createObjectAssociation$(
    objectType: ObjectType,
    objectId: string,
    associatedId: string,
    associationType: AssociationType,
    tags?: string[],
    objectSubtype?: string,
    associationSubtype?: string,
  ): Observable<CreateAssociationResponse | null> {
    const namespace$ = this.config.namespace$;
    const direction = getAssociationDirection(objectType, associationType, objectSubtype, associationSubtype);
    return namespace$.pipe(
      switchMap((namespace) => {
        const fromId = direction === AssociationDirection.From ? objectId : associatedId;
        const fromSubtype = direction === AssociationDirection.From ? objectSubtype : associationSubtype;
        const toId = direction === AssociationDirection.From ? associatedId : objectId;
        const toSubtype = direction === AssociationDirection.From ? associationSubtype : objectSubtype;

        const requestTags = tags
          ? tags.map((tag) => {
              return {
                tagValue: tag,
              };
            })
          : ([] as { tagValue: string }[]);

        return this.crmAssociationApiService.createAssociation({
          namespace: namespace,
          association: {
            associationType: associationType,
            fromId: fromId,
            toId: toId,
            tags: requestTags,
            fromCrmObjectSubtype: fromSubtype,
            toCrmObjectSubtype: toSubtype,
          },
        });
      }),
      catchError((_) => {
        return of(null);
      }),
    );
  }

  listAssociationFieldOptions(
    associationType: string,
    fieldId: string,
    pageSize: number,
    searchTerm: string,
  ): Observable<string[]> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.crmAssociationApiService.listAssociationFieldOptions({
          namespace,
          associationType,
          fieldId,
          pageSize,
          searchTerm,
        });
      }),
      map((response) => {
        return response.options ?? [];
      }),
    );
  }

  updateAssociationTags(id: string, tags: string[]): Observable<boolean> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.crmAssociationApiService.updateAssociation({
          namespace,
          associationId: id,
          tags: tags.map((t) => ({ tagValue: t })),
        });
      }),
      map(() => true),
    );
  }

  getAssociation(id: string): Observable<AssociationInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.crmAssociationApiService.getMultiAssociation({
          namespace,
          associationIds: [id],
        });
      }),
      map((response) =>
        response?.associations?.length > 0 && response?.associations[0] ? response.associations[0] : {},
      ),
    );
  }

  getAssociationsForObject$(
    objectId: string,
    objectType: ObjectType,
    associatedType: ObjectType,
    pageSize: number,
    withTags: string[] = [],
    withFromType = '',
    withToType = '',
  ): Observable<AssociationInterface[]> {
    const namespace$ = this.config.namespace$;
    const associationType = getAssociationType(objectType, associatedType);
    const direction = getAssociationDirection(objectType, associationType, withFromType, withToType);

    const filters = [
      {
        fieldId: direction === AssociationDirection.From ? 'from_id' : 'to_id',
        operator: FilterOperator.FILTER_OPERATOR_IS,
        values: [
          {
            string: objectId,
          },
        ],
      },
      {
        fieldId: 'association_type',
        operator: FilterOperator.FILTER_OPERATOR_IS,
        values: [
          {
            string: associationType,
          },
        ],
      },
    ];

    if (withTags.length > 0) {
      filters.push({
        fieldId: 'tags',
        operator: FilterOperator.FILTER_OPERATOR_IS_ALL,
        values: withTags.map((t) => ({ string: t })),
      });
    }

    if (withToType) {
      filters.push({
        fieldId: direction === AssociationDirection.From ? 'to_crm_object_subtype' : 'from_crm_object_subtype',
        operator: FilterOperator.FILTER_OPERATOR_IS,
        values: [
          {
            string: withToType,
          },
        ],
      });
    }

    return namespace$.pipe(
      switchMap((namespace) =>
        this.paginateListAssociation$(
          namespace,
          {
            operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
            filters: filters,
          },
          pageSize,
        ),
      ),
    );
  }

  getAssociationsWithAssociatedObjectForObject$(
    objectId: string,
    objectType: ObjectType,
    associatedType: ObjectType,
    pageSize = 5,
    withTags: string[] = [],
    withFromType = '',
    withToType = '',
  ): Observable<Association[]> {
    if (!objectId) {
      return of([]);
    }
    const associationType = getAssociationType(objectType, associatedType);
    const direction = getAssociationDirection(objectType, associationType, withFromType, withToType);

    const associations$ = this.getAssociationsForObject$(objectId, objectType, associatedType, pageSize, withTags);

    const oppDirection = direction === AssociationDirection.From ? AssociationDirection.To : AssociationDirection.From;
    const objects$ = this.getObjectsForAssociations$(associatedType, associations$, oppDirection);
    return this.joinAssociationsWithObjects$(associations$, objects$, oppDirection);
  }

  private chunkArray<T>(arr: T[]): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += CrmAssociationService.CHUNK_SIZE) {
      chunks.push(arr.slice(i, i + CrmAssociationService.CHUNK_SIZE));
    }
    return chunks;
  }

  private getMultiAssociationChunked$(namespace: string, associationIds: string[]): Observable<AssociationInterface[]> {
    return forkJoin(
      this.chunkArray(associationIds).map((chunk) =>
        this.crmAssociationApiService.getMultiAssociation({ namespace, associationIds: chunk }),
      ),
    ).pipe(map((responses) => responses.flatMap((r) => r?.associations ?? [])));
  }

  private getMultiObjectChunked$(objectType: ObjectType, ids: string[]): Observable<CrmObjectInterface[]> {
    return forkJoin(this.chunkArray(ids).map((chunk) => this.objectService.getMultiObject(objectType, chunk))).pipe(
      map((responses) => responses.flatMap((r) => r?.crmObjects ?? [])),
    );
  }

  private paginateListAssociation$(
    namespace: string,
    filtersV2: ListAssociationsRequestInterface['filtersV2'],
    pageSize: number,
  ): Observable<AssociationInterface[]> {
    const seed: ListAssociationsResponseInterface = {
      associations: [],
      pagingMetadata: { hasMore: true, nextCursor: '' },
    };
    return of(seed).pipe(
      expand((resp) => {
        if (!resp.pagingMetadata?.hasMore) {
          return EMPTY;
        }
        return this.crmAssociationApiService.listAssociation({
          namespace,
          pagingOptions: { pageSize, cursor: resp.pagingMetadata?.nextCursor },
          filtersV2,
        } as ListAssociationsRequestInterface);
      }),
      take(101), // seed + max 100 pages
      reduce(
        (acc: AssociationInterface[], val: ListAssociationsResponseInterface): AssociationInterface[] => [
          ...acc,
          ...(val?.associations || []),
        ],
        [] as AssociationInterface[],
      ),
    );
  }

  private getObjectsForAssociations$(
    objectType: ObjectType,
    associations$: Observable<AssociationInterface[]>,
    direction: AssociationDirection,
  ): Observable<CrmObjectInterface[]> {
    return associations$.pipe(
      switchMap((associations) => {
        if (!associations?.length) {
          return of([] as CrmObjectInterface[]);
        }
        const ids = associations.map((a) =>
          direction === AssociationDirection.From ? a.fromId || '' : a.toId || '',
        );
        return this.getMultiObjectChunked$(objectType, ids);
      }),
    );
  }
  getAssociationsFromAssociationIds$(
    ids: string[],
    objectType: ObjectType,
    associatedType: ObjectType,
    withFromType = '',
    withToType = '',
  ): Observable<Association[]> {
    if (ids.length === 0) {
      return of([]);
    }
    const namespace$ = this.config.namespace$;
    const associationType = getAssociationType(objectType, associatedType);
    const direction = getAssociationDirection(objectType, associationType, withFromType, withToType);

    const associations$ = namespace$.pipe(
      switchMap((namespace) => this.getMultiAssociationChunked$(namespace, ids)),
    );

    const oppDirection = direction === AssociationDirection.From ? AssociationDirection.To : AssociationDirection.From;
    const objects$ = this.getObjectsForAssociations$(associatedType, associations$, oppDirection);
    return this.joinAssociationsWithObjects$(associations$, objects$, oppDirection);
  }
}
